/**
 * Discuss-first helpers (docs/ux/project-tab-spec.md §1.2, §1.3a): mode prefixes, typed "do it",
 * explicit topic markers, the per-segment action list and the proposal digest the device signs
 * on confirm (the daemon executes only what matches it).
 */
import { hashCanonicalB64 } from '@viko/crypto'
import {
  type BarkMode,
  type BarkProposal,
  type BarkRecord,
  hasExplicitSplitMarker,
  PROPOSAL_DIGEST_DOMAIN,
  type ProposedOp,
  proposalDigestInput,
} from '@viko/protocol'

/** `?? text` or `/discuss text` sends as Discuss; the prefix is not part of the bark. */
export function parseBarkInput(raw: string, mode: BarkMode): { text: string; mode: BarkMode } {
  const t = raw.trim()
  const m = /^(\?\?\s+|\/discuss\b\s*)/i.exec(t)
  if (m) return { text: t.slice(m[0].length).trim(), mode: 'discuss' }
  return { text: t, mode }
}

/** "do it" / "go" typed in a conversation confirms the latest open proposal. */
export function isDoIt(text: string): boolean {
  return /^(do it|go|go ahead|yes,? do it|ship it)[.!]?$/i.test(text.trim())
}

export function latestOpenProposal(b: BarkRecord): BarkProposal | undefined {
  return [...b.proposals].reverse().find((p) => p.status === 'open')
}

/** A message that starts a new topic (explicit marker, P1): split from here. */
export const startsNewTopic = hasExplicitSplitMarker

export function proposalDigest(p: BarkProposal, actions: ProposedOp[] = p.actions) {
  return hashCanonicalB64(PROPOSAL_DIGEST_DOMAIN, proposalDigestInput({ ...p, actions }))
}

/** "Actions from this conversation: ✓ 2 tasks · ◑ steer sent · ○ proposal open". */
export function actionList(b: BarkRecord): { glyph: '✓' | '◑' | '○' | '↺'; text: string }[] {
  const out: { glyph: '✓' | '◑' | '○' | '↺'; text: string }[] = []
  for (const a of b.actions) {
    const tasks = a.targets.filter((t) => t.type === 'task').length
    const text =
      a.decision === 'steered'
        ? 'steer sent'
        : a.decision === 'answered'
          ? 'answer saved'
          : tasks
            ? `${tasks} task${tasks === 1 ? '' : 's'}`
            : a.decision
    out.push({ glyph: a.status === 'active' ? (a.decision === 'steered' ? '◑' : '✓') : '↺', text })
  }
  for (const p of b.proposals)
    if (p.status === 'open') out.push({ glyph: '○', text: 'proposal open' })
  return out
}

/** Human line for one typed proposal action. */
export function describeOp(op: ProposedOp): string {
  switch (op.op) {
    case 'task.create':
      return `${op.title}${op.autoLand ? ' (auto-lands on green)' : ''}`
    case 'run.steer':
      return `Tell the live run: “${op.text}”`
    case 'answer.save':
      return 'Save this answer to the project notes'
    case 'park':
      return `Park it${op.note ? `: ${op.note}` : ''}`
    case 'task.priority':
      return `Set priority ${op.priority}`
  }
}
